import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/form/Form';
import { useCustomerRoutePrefix } from '@/services/customer/useCustomerRoutePrefix';
import { useNavigate, useParams } from 'react-router';
import { useCompany } from '@/services/api/hooks/companies';
import { TextField } from '@/components/form/fields/text/TextInput';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Button } from '@/components/form/Button';
import { useSubmitStockReportingRequest } from '@/services/api/hooks/management';

const FormFields = z.object({
  customerName: z.string().nonempty()
});

type FormFields = z.infer<typeof FormFields>;

export const StockReportingPage: FC = () => {
  const breadcrumbs: NavPage[] = [
    {
      name: 'Management',
      to: ''
    },
    {
      name: 'Stock Reporting',
      to: ''
    }
  ];

  const { partnerId, companyId } = useParams();

  if (!(partnerId && companyId)) throw new Error('Missing params');
  const [company] = useCompany(partnerId, companyId);

  const methods = useForm<FormFields>({
    resolver: zodResolver(FormFields),
    defaultValues: {
      customerName: company.name
    }
  });

  const navigate = useNavigate();
  const routePrefix = useCustomerRoutePrefix();

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const submitRequest = useSubmitStockReportingRequest();

  const onSubmit = async (data: FormFields) => {
    setLoading(true);
    setErrorMessage("");

    const result = await submitRequest.mutateAsync({
      customerName: data.customerName
    });
    
    if (result.status === "Message sent.") {
      navigate(`${routePrefix}/management/success`);
    } else {
      setErrorMessage("Unable to submit request.");
      setLoading(false);
    }
  };

  return (
    <>
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex justify-between gap-5 items-center font-bold">
          <h3 className="text-3xl">Stock Reporting</h3>
        </div>
      </BreadcrumbPageHeader>
      <div className="flex-1 overflow-y-auto p-10">
        Coming soon!
        <Form methods={methods} onSubmit={onSubmit} className="hidden space-y-5">
          <TextField id="customerName" label="Customer Name" readonly />
          {errorMessage && <p className='text-red-500'>{errorMessage}</p>}
          <Button variant={ButtonVariant.Primary} type="submit" loading={loading}>
            Submit
          </Button>
        </Form>
      </div>
    </>
  );
};
